import { ReactNode, createContext, useRef, useState } from 'react';

// ----------------------------------------------------------------------

type ScrollingContextProps = {
    onHandleScroll: (elem: any) => void,
    onResetScroll: () => void,
    scrollTop: number,
    scrollOnTop: boolean,
    scrollingUp: boolean,
    scrollingDown: boolean,
    headerCollapsed: boolean,
};

const initialState: ScrollingContextProps = {
    onHandleScroll: () => { },
    onResetScroll: () => { },
    scrollTop: 0,
    scrollOnTop: true,
    scrollingUp: false,
    scrollingDown: false,
    headerCollapsed: false,
};

const ScrollingContext = createContext<ScrollingContextProps>(initialState);

type ScrollingProviderProps = {
    children: ReactNode;
};

const TOP_THRESHOLD = 10;
const MIN_DELTA = 2;

const ScrollingProvider = ({ children }: ScrollingProviderProps) => {
    const [scrollTop, setScrollTop] = useState(0);
    const [scrollOnTop, setScrollOnTop] = useState<boolean>(true);
    const [scrollingUp, setScrollingUp] = useState<boolean>(false);
    const [scrollingDown, setScrollingDown] = useState<boolean>(false);
    const [headerCollapsed, setHeaderCollapsed] = useState<boolean>(false);

    const prevScrollTopRef = useRef<number | null>(null);
    const prevClientHeightRef = useRef<number | null>(null);

    const handleScroll = (e: any) => {
        const { scrollTop, clientHeight } = e.currentTarget;

        const prevTop = prevScrollTopRef.current;
        const prevHeight = prevClientHeightRef.current;
        prevScrollTopRef.current = scrollTop;
        prevClientHeightRef.current = clientHeight;

        setScrollTop(scrollTop);

        const onTop = scrollTop < TOP_THRESHOLD;
        setScrollOnTop(onTop);

        if (onTop) {
            setScrollingDown(false);
            setScrollingUp(false);
            setHeaderCollapsed(false);
            return;
        }

        if (prevTop === null || prevHeight !== clientHeight) return;

        const delta = scrollTop - prevTop;

        const down = delta > 0;
        setScrollingDown(down);
        setScrollingUp(!down);

        if (down) setHeaderCollapsed(true);
    };

    const resetScroll = () => {
        prevScrollTopRef.current = null;
        prevClientHeightRef.current = null;
        setScrollTop(0);
        setScrollOnTop(true);
        setScrollingUp(false);
        setScrollingDown(false);
        setHeaderCollapsed(false);
    };

    return (
        <ScrollingContext.Provider
            value={{
                scrollTop,
                scrollOnTop,
                scrollingUp,
                scrollingDown,
                headerCollapsed,
                onHandleScroll: handleScroll,
                onResetScroll: resetScroll,
            }}
        >
            {children}
        </ScrollingContext.Provider>
    );
};

export { ScrollingProvider, ScrollingContext };